import { useMemo } from 'react';
import { FrontendStaffUser } from '../../../../services/staff/staffService';
import { AccessRequestStatus } from '../../../../stores/appStaffStore';

interface UseFilteredStaffArgs {
  staff: FrontendStaffUser[];
  statusFilter: AccessRequestStatus | 'all';
  /** Empty means "all restaurants" — no restaurant filtering is applied. */
  restaurantFilters: string[];
  searchQuery: string;
  getRestaurantName: (id: string) => string;
}

/**
 * Status + restaurant + free-text filtering, sorted by each member's
 * alphabetically-first restaurant name.
 */
export function useFilteredStaff({
  staff,
  statusFilter,
  restaurantFilters,
  searchQuery,
  getRestaurantName,
}: UseFilteredStaffArgs): FrontendStaffUser[] {
  return useMemo(() => {
    let filtered = statusFilter === 'all' ? staff : staff.filter((u) => u.access_request === statusFilter);

    if (restaurantFilters.length > 0) {
      filtered = filtered.filter((u) => u.restaurant_ids.some((rid) => restaurantFilters.includes(rid)));
    }

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter((u) => {
        const haystack = [u.name, u.email, u.restaurant_role].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(query);
      });
    }

    const firstRestaurantName = (user: FrontendStaffUser) =>
      user.restaurant_ids
        .map((id) => getRestaurantName(id))
        .sort((x, y) => x.localeCompare(y))[0] || '';

    return [...filtered].sort((a, b) => firstRestaurantName(a).localeCompare(firstRestaurantName(b)));
  }, [staff, restaurantFilters, statusFilter, searchQuery, getRestaurantName]);
}
